import { SurveyInputTypeEnum } from "../types/forms";
import { evaluateValueExpression } from "./elements-condition";
import { getElementValueKey } from "./helpers";
/**
 * `minMaxTypes` são os tipos de `<input>`
 * para os quais `min`/`max` fazem sentido.
 */
var MIN_MAX_INPUT_TYPES = [
    SurveyInputTypeEnum.NUMBER,
    SurveyInputTypeEnum.RANGE,
    SurveyInputTypeEnum.DATE,
    SurveyInputTypeEnum.DATETIME_LOCAL,
    SurveyInputTypeEnum.MONTH,
    SurveyInputTypeEnum.TIME,
    SurveyInputTypeEnum.WEEK,
];
var DATE_INPUT_TYPES = [
    SurveyInputTypeEnum.DATE,
    SurveyInputTypeEnum.DATETIME_LOCAL,
    SurveyInputTypeEnum.MONTH,
    SurveyInputTypeEnum.WEEK,
];
export var DEFAULT_DATE_MAX = "3999-12-31";
export var isMinMaxInputType = function (inputType) {
    return !!inputType && MIN_MAX_INPUT_TYPES.includes(inputType);
};
export var isDateInputType = function (inputType) {
    return !!inputType && DATE_INPUT_TYPES.includes(inputType);
};
var extractExpression = function (literal, expression) {
    if (typeof literal === "string" && literal.startsWith("=")) {
        return literal.slice(1);
    }
    return expression || undefined;
};
var normalizeLimit = function (value, inputType) {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "boolean") {
        return undefined;
    }
    var text = String(value);
    if (!isDateInputType(inputType)) {
        return text;
    }
    // O limite precisa sair no formato que o próprio `<input>` usa, senão o
    // navegador descarta o atributo e o seletor deixa de ser limitado.
    var _a = text.split("T"), datePart = _a[0], timePart = _a[1];
    if (inputType === SurveyInputTypeEnum.DATE) {
        return datePart;
    }
    if (inputType === SurveyInputTypeEnum.MONTH) {
        return datePart.slice(0, 7);
    }
    if (inputType === SurveyInputTypeEnum.DATETIME_LOCAL) {
        // `currentDate()` traz segundos; o campo trabalha em minutos.
        return "".concat(datePart, "T").concat((timePart || "00:00").slice(0, 5));
    }
    return text;
};
var resolveLimit = function (literal, expression, formValues, inputType) {
    var valueExpression = extractExpression(literal, expression);
    if (valueExpression) {
        return normalizeLimit(evaluateValueExpression(valueExpression, formValues), inputType);
    }
    return normalizeLimit(literal, inputType);
};
export var resolveElementMinMax = function (element, formValues) {
    var inputType = element.inputType;
    if (!isMinMaxInputType(inputType)) {
        return {};
    }
    var min = resolveLimit(element.min, element.minValueExpression, formValues, inputType);
    var max = resolveLimit(element.max, element.maxValueExpression, formValues, inputType);
    var appliesDefaultMax = inputType === SurveyInputTypeEnum.DATE ||
        inputType === SurveyInputTypeEnum.DATETIME_LOCAL;
    if (max === undefined && appliesDefaultMax) {
        return { min: min, max: normalizeLimit(DEFAULT_DATE_MAX, inputType) };
    }
    return { min: min, max: max };
};
export var compareMinMaxValue = function (value, limit, inputType) {
    if (isDateInputType(inputType)) {
        var valueTime = new Date(String(value)).getTime();
        var limitTime = new Date(String(limit)).getTime();
        if (isNaN(valueTime) || isNaN(limitTime)) {
            return 0;
        }
        return valueTime - limitTime;
    }
    var numericValue = Number(value);
    var numericLimit = Number(limit);
    if (!isNaN(numericValue) && !isNaN(numericLimit)) {
        return numericValue - numericLimit;
    }
    return String(value).localeCompare(String(limit));
};
export var formatMinMaxForMessage = function (limit, inputType) {
    var text = String(limit);
    if (inputType === SurveyInputTypeEnum.MONTH) {
        var month_1 = /^(\d{4})-(\d{2})$/.exec(text);
        return month_1 ? "".concat(month_1[2], "/").concat(month_1[1]) : text;
    }
    if (inputType !== SurveyInputTypeEnum.DATE &&
        inputType !== SurveyInputTypeEnum.DATETIME_LOCAL) {
        return text;
    }
    var match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(text);
    if (!match) {
        return text;
    }
    var year = match[1], month = match[2], day = match[3], hour = match[4], minute = match[5];
    var date = "".concat(day, "/").concat(month, "/").concat(year);
    return inputType === SurveyInputTypeEnum.DATETIME_LOCAL && hour
        ? "".concat(date, ", ").concat(hour, ":").concat(minute)
        : date;
};
export var DEFAULT_MIN_ERROR_TEXT = "O valor não deverá ser menor que {0}";
export var DEFAULT_MAX_ERROR_TEXT = "O valor não deverá ser maior que {0}";
export var buildMinMaxErrorText = function (template, fallback, limit, inputType) {
    return (template || fallback).replace("{0}", formatMinMaxForMessage(limit, inputType));
};
/**
 * Paridade com `isValueLessMin` / `isValueGreaterMax`:
 * só valida com valor preenchido e limite presente.
 */
export var checkMinMaxViolation = function (element, value, limits) {
    var inputType = element.inputType;
    if (!isMinMaxInputType(inputType)) {
        return undefined;
    }
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    var comparableValue = value;
    if (limits.min !== undefined &&
        compareMinMaxValue(comparableValue, limits.min, inputType) < 0) {
        return {
            kind: "min",
            message: buildMinMaxErrorText(element.minErrorText, DEFAULT_MIN_ERROR_TEXT, limits.min, inputType),
        };
    }
    if (limits.max !== undefined &&
        compareMinMaxValue(comparableValue, limits.max, inputType) > 0) {
        return {
            kind: "max",
            message: buildMinMaxErrorText(element.maxErrorText, DEFAULT_MAX_ERROR_TEXT, limits.max, inputType),
        };
    }
    return undefined;
};
/**
 * Varre `min`/`max` de uma lista de elementos.
 *
 * A validação de limites vive no schema Zod do campo, que só é alcançado por
 * `trigger()` em campos registrados, páginas ainda não montadas escapariam.
 */
export var validateElementLimits = function (elements, formValues) {
    var results = [];
    for (var _i = 0, elements_1 = elements; _i < elements_1.length; _i++) {
        var element = elements_1[_i];
        if (!isMinMaxInputType(element.inputType)) {
            continue;
        }
        var limits = resolveElementMinMax(element, formValues);
        var valueKey = getElementValueKey(element);
        var violation = checkMinMaxViolation(element, formValues[valueKey], limits);
        if (violation) {
            results.push({
                isValid: false,
                errorElement: valueKey,
                errorText: violation.message,
            });
        }
    }
    return results;
};
